import React, { useState, useEffect } from 'react';
import { Book, Film, Plus, Edit, Trash2, Eye, AlertCircle, Search, CheckCircle, XCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '../../lib/supabase';
import { ChristianTip, fetchAllChristianTips, deleteChristianTip, toggleChristianTipStatus } from '../../lib/christian-tips';
import ChristianTipForm from '../../components/ChristianTipForm';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function AdminChristianTips() {
  const [tips, setTips] = useState<ChristianTip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTip, setEditingTip] = useState<ChristianTip | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tipToDelete, setTipToDelete] = useState<ChristianTip | null>(null);
  
  const perPage = 10;

  useEffect(() => {
    fetchTips();
  }, [currentPage, selectedType, selectedStatus]);

  async function fetchTips() {
    try {
      setLoading(true);
      setError('');
      
      const { data, count } = await fetchAllChristianTips({
        type: selectedType as 'livro' | 'filme' | undefined,
        status: selectedStatus as 'ativo' | 'inativo' | undefined,
        page: currentPage,
        perPage,
        orderBy: 'published_at',
        orderDirection: 'desc'
      });
      
      setTips(data);
      setTotalCount(count);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar recomendações');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateTip(tipData: any) {
    try {
      setError('');
      
      const { data, error } = await supabase
        .from('christian_tips')
        .insert([{
          ...tipData,
          user_id: (await supabase.auth.getSession()).data.session?.user.id
        }]);
      
      if (error) throw error;
      
      setSuccess('Recomendação criada com sucesso!');
      setIsFormOpen(false);
      fetchTips();
    } catch (err: any) {
      setError(err.message || 'Erro ao criar recomendação');
    }
  }

  async function handleUpdateTip(tipData: any) {
    if (!editingTip) return;
    
    try {
      setError('');
      
      const { data, error } = await supabase
        .from('christian_tips')
        .update(tipData)
        .eq('id', editingTip.id);
      
      if (error) throw error;
      
      setSuccess('Recomendação atualizada com sucesso!');
      setIsFormOpen(false);
      setEditingTip(undefined);
      fetchTips();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar recomendação');
    }
  }

  async function handleDeleteTip() {
    if (!tipToDelete) return;
    
    try {
      setError('');
      
      const success = await deleteChristianTip(tipToDelete.id);
      
      if (!success) throw new Error('Erro ao excluir recomendação');
      
      setSuccess('Recomendação excluída com sucesso!');
      setIsDeleteModalOpen(false);
      setTipToDelete(null);
      fetchTips();
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir recomendação');
    }
  }

  async function handleToggleStatus(tip: ChristianTip) {
    try {
      setError('');
      
      const newStatus = tip.status === 'ativo' ? 'inativo' : 'ativo';
      const success = await toggleChristianTipStatus(tip.id, newStatus);
      
      if (!success) throw new Error(`Erro ao ${newStatus === 'ativo' ? 'ativar' : 'desativar'} recomendação`);
      
      setSuccess(`Recomendação ${newStatus === 'ativo' ? 'ativada' : 'desativada'} com sucesso!`);
      fetchTips();
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar status da recomendação');
    }
  }

  function handleEdit(tip: ChristianTip) {
    setEditingTip(tip);
    setIsFormOpen(true);
  }

  function handleCloseForm() {
    setIsFormOpen(false);
    setEditingTip(undefined);
  }

  function confirmDelete(tip: ChristianTip) {
    setTipToDelete(tip);
    setIsDeleteModalOpen(true);
  }

  // Filtra os resultados localmente com base na busca
  const filteredTips = tips.filter(tip => 
    searchTerm ? 
      tip.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      tip.description.toLowerCase().includes(searchTerm.toLowerCase())
    : true
  );

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <AdminLayout title="Gerenciar Dicas Cristãs">
      {isFormOpen ? (
        <ChristianTipForm
          initialData={editingTip}
          onSubmit={editingTip ? handleUpdateTip : handleCreateTip}
          onCancel={handleCloseForm}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-brand-teal flex items-center gap-2">
              <Book className="h-5 w-5" />
              Dicas Cristãs
            </h2>

            <button
              onClick={() => setIsFormOpen(true)}
              className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nova Recomendação
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
              <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <div className="mb-6 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar recomendações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              />
            </div>

            <select
              value={selectedType || ''}
              onChange={(e) => {
                setSelectedType(e.target.value || null);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value="">Todos os tipos</option>
              <option value="livro">Livros</option>
              <option value="filme">Filmes</option>
            </select>

            <select
              value={selectedStatus || ''}
              onChange={(e) => {
                setSelectedStatus(e.target.value || null);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value="">Todos os status</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Título</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Tipo</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Classificação</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Publicação</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredTips.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      Nenhuma recomendação encontrada
                    </td>
                  </tr>
                ) : (
                  filteredTips.map((tip) => (
                    <tr key={tip.id} className="border-b border-gray-200">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-16 bg-gray-200 rounded overflow-hidden">
                            <img
                              src={tip.cover_image}
                              alt={tip.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80';
                              }}
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{tip.title}</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{tip.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {tip.type === 'livro' ? (
                            <>
                              <Book className="h-3 w-3 mr-1" />
                              Livro
                            </>
                          ) : (
                            <>
                              <Film className="h-3 w-3 mr-1" />
                              Filme
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {tip.age_rating}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          tip.status === 'ativo'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {tip.status === 'ativo' ? (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Ativo
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 mr-1" />
                              Inativo
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-500">
                        {format(new Date(tip.published_at), 'dd/MM/yyyy', { locale: ptBR })}
                      </td>
                      <td className="px-4 py-4 text-right space-x-1">
                        <button
                          onClick={() => handleToggleStatus(tip)}
                          className={`${
                            tip.status === 'ativo'
                              ? 'text-red-500 hover:text-red-600'
                              : 'text-green-500 hover:text-green-600'
                          } p-1`}
                          title={tip.status === 'ativo' ? 'Desativar' : 'Ativar'}
                        >
                          {tip.status === 'ativo' ? (
                            <XCircle className="h-4 w-4" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(tip)}
                          className="text-brand-teal hover:text-brand-teal/80 p-1"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => confirmDelete(tip)}
                          className="text-red-500 hover:text-red-600 p-1"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <a
                          href={`/christian-tips?preview=${tip.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-gray-700 p-1"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                >
                  Anterior
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-full ${
                      currentPage === page
                        ? 'bg-brand-teal text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {isDeleteModalOpen && tipToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir a recomendação <strong>"{tipToDelete.title}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteTip}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}